/**
 * The Realism Panel's state — the whole of `SIMREAL.BOX`'s controls as data,
 * and the one place a single mission or a career sortie gets its difficulty
 * from.
 *
 * The panel is the original's own settings surface for flying, so this
 * replaces the modern per-flight toggles: `/missions` and `/career` write here
 * and `/fly` reads it back through the mission it is handed, without either
 * route learning a new parameter. That works because a `MissionSpec` already
 * carries `tier` and `realism`, so `applyRealism` can stamp the player's
 * choices onto a freshly generated spec and everything downstream —
 * `encounterSetupFrom`, `flightOptionsFor`, the gauges — reads them as it
 * always has. See `missionFor` and `careerSortie`, the two functions that
 * build a spec for `/fly`.
 *
 * Eleven checkboxes, two value selectors and three presets, matching the
 * capture. Eight of the checkboxes are `packages/engine`'s `RealismSettings`
 * and drive the sim outright. `REALISTIC INSTRUMENTS` is already persisted by
 * `flight/cockpit-settings.ts` and consumed by the cockpit, so this module
 * writes *through* to that store rather than keeping a second copy of the same
 * bit. `SUN BLIND SPOT` and `REAL NAVIGATION` are the other two view-layer
 * toggles: `routes/Fly.tsx` reads `extra` straight off this panel (not through
 * `MissionSpec` — they are presentation, not sim state) and hands both to
 * `createFlightScene`, which suppresses the enemy contact near the sun
 * (`flight/sun-blind-spot.ts`, RE evidence and CALIBRATION-PENDING cone in
 * `docs/engineering/sun-blind-spot-design.md`) and drops the waypoint cue
 * (`flight/mission-hud.ts`'s `MissionHudOptions.hideWaypoint`) respectively.
 * `COMBAT LEVEL` is stored, shown and scored, but no system consumes it yet:
 * AI skill comes from the mission generator rather than a global. Kept on the
 * panel because the panel is the original's, and named here so the gap is a
 * work item instead of a surprise.
 *
 * `REALISTIC WEATHER` moved from that list into the sim half in the wind wave:
 * AD-007k found that `RB.EXE`'s flag for it is what decides whether a wind model
 * is installed at all, so it is now `RealismSettings.realisticWeather` and
 * drives real drift rather than being stored and ignored. Since AD-007k §10 it
 * is the switch on a **live westerly**: on (the panel default, which is what
 * both shipped `.PRF` files carry) the mission's briefed wind word blows for
 * real — nothing at `still`, 13 mph at `light`, 20 mph at `strong` — and off is
 * dead air on every sortie. It is the only control the player has over the wind,
 * exactly as in the original.
 */

import {
  ALL_REALISM_OFF,
  COMBAT_LEVELS,
  FLIGHT_MODEL_TIERS,
  type CombatLevel,
  type FlightModelTier,
  MISSION_REALISM,
  type MissionSpec,
  type RealismSettings,
  REALISM_PRESETS,
} from "@red-opus/engine";
import type { SettingsStore } from "../audio/settings.ts";
import { loadCockpitSettings, saveCockpitSettings } from "../flight/cockpit-settings.ts";

export const REALISM_STORAGE_KEY = "red-opus/realism-panel";

/**
 * `COMBAT LEVEL`'s values, and the same labels `combat/flak.ts` scales flak
 * damage by (`FLAK_COMBAT_LEVEL_SCALE`, AD-010 §9.4) — re-exported here rather
 * than redefined so the panel and the sim can never disagree on what `"EASY"`
 * means. `EASY` is what the capture shows at the `NOVICE` preset.
 */
export { COMBAT_LEVELS };
export type { CombatLevel };

/** The three toggles that are not in `packages/engine`'s `RealismSettings`. */
export interface PresentationRealism {
  readonly realisticInstruments: boolean;
  readonly sunBlindSpot: boolean;
  readonly realNavigation: boolean;
}

export interface RealismPanel {
  readonly tier: FlightModelTier;
  readonly combatLevel: CombatLevel;
  readonly sim: RealismSettings;
  readonly extra: PresentationRealism;
  /**
   * `PS.EXE`'s own `START AT YOUR AERODROME` / `START NEAR THE ACTION` choice
   * (roadmap 10a) — strings recovered verbatim from both the floppy and CD
   * `PS.EXE` (`strings` on `data/original/RedBaron/SIERRA/REDBARON/PS.EXE`
   * and `data/extracted/cd/REDBARON/PS.EXE`, sitting together with a
   * `CANCEL` in the mission-setup string cluster). `false` is
   * `START NEAR THE ACTION` — this port's shipped default and the original's
   * own single-mission default — so an untouched setting changes no flight,
   * matching every other field on this panel. Unlike the rest of the panel,
   * this one has to reach `generateMission` itself rather than being stamped
   * on afterwards (`mission-launch.ts`'s `missionFor`, `career-session.ts`'s
   * `careerSortie`): it moves where the flight starts, not how it flies.
   */
  readonly aerodromeStart: boolean;
}

/** Verbatim from `PS.EXE`'s string dump — the two states of the toggle above. */
export const AERODROME_START_LABELS = {
  on: "START AT YOUR AERODROME",
  off: "START NEAR THE ACTION",
} as const;

/**
 * Every checkbox on the panel, left column top-to-bottom then right column —
 * the order `realism-panel.png` reads in. The right column has five, not six:
 * its last slot is the `COMBAT LEVEL` selector, which is why `SIMREAL.BOX`
 * has eleven 7×7 rects and not twelve.
 */
export const REALISM_TOGGLE_IDS = [
  "realisticInstruments",
  "sunBlindSpot",
  "realisticWeather",
  "gunJams",
  "blackouts",
  "midairCollisions",
  "carburetorFreezes",
  "limitedAmmunition",
  "limitedFuel",
  "realNavigation",
  "aircraftMayBeDamaged",
] as const;

/** How many of them are in the left column. */
export const REALISM_LEFT_COLUMN = 6;

export type RealismToggleId = (typeof REALISM_TOGGLE_IDS)[number];

/** Verbatim from `realism-panel.png`. */
export const REALISM_TOGGLE_LABELS: Readonly<Record<RealismToggleId, string>> = {
  realisticInstruments: "REALISTIC INSTRUMENTS",
  carburetorFreezes: "CARBURETOR FREEZES",
  sunBlindSpot: "SUN BLIND SPOT",
  limitedAmmunition: "LIMITED AMMUNITION",
  realisticWeather: "REALISTIC WEATHER",
  limitedFuel: "LIMITED FUEL",
  gunJams: "GUN JAMS ALLOWED",
  realNavigation: "REAL NAVIGATION",
  blackouts: "BLACKOUTS ALLOWED",
  aircraftMayBeDamaged: "AIRCRAFT MAY BE DAMAGED",
  midairCollisions: "MIDAIR COLLISIONS",
};

const EXTRA_IDS: readonly RealismToggleId[] = ["realisticInstruments", "sunBlindSpot", "realNavigation"];

function isExtra(id: RealismToggleId): id is keyof PresentationRealism {
  return EXTRA_IDS.includes(id);
}

export function toggleValue(panel: RealismPanel, id: RealismToggleId): boolean {
  return isExtra(id) ? panel.extra[id] : panel.sim[id];
}

export function withToggle(panel: RealismPanel, id: RealismToggleId, value: boolean): RealismPanel {
  if (isExtra(id)) return { ...panel, extra: { ...panel.extra, [id]: value } };
  return { ...panel, sim: { ...panel.sim, [id]: value } };
}

/**
 * The presentation half of each preset. The engine already itemises the sim
 * half (`REALISM_PRESETS`); these four are chosen so the derived
 * `OVERALL DIFFICULTY` readout reproduces the ladder measured live —
 * `NOVICE 4 → INTERMEDIATE 7 → EXPERT 9` — which is the only evidence there
 * is about what the middle preset contains.
 */
const PRESET_EXTRAS: Readonly<Record<FlightModelTier, PresentationRealism>> = {
  novice: { realisticInstruments: false, sunBlindSpot: false, realNavigation: false },
  intermediate: { realisticInstruments: false, sunBlindSpot: true, realNavigation: false },
  expert: { realisticInstruments: true, sunBlindSpot: true, realNavigation: true },
};

export function presetPanel(
  tier: FlightModelTier,
  combatLevel: CombatLevel = "EASY",
  aerodromeStart = false,
): RealismPanel {
  return { tier, combatLevel, sim: REALISM_PRESETS[tier], extra: PRESET_EXTRAS[tier], aerodromeStart };
}

/**
 * The port's starting point is not one of the three presets: it is exactly
 * what the mission generators already stamp on every spec
 * (`MISSION_REALISM` — the manual's Intermediate settings, which have MIDAIR
 * COLLISIONS off; the encounter layer does model ramming, see AD-017).
 * Keeping it identical is deliberate: a player who never opens the
 * panel flies precisely the mission the generator built, and this lane
 * changes no flight.
 */
export const DEFAULT_REALISM: RealismPanel = {
  tier: "intermediate",
  // AVERAGE, not the original panel's EASY: a port decision (user, 2026-08-21)
  // so archie can hurt an out-of-the-box player. The NOVICE preset still
  // resets to EASY, matching the capture.
  combatLevel: "AVERAGE",
  sim: { ...ALL_REALISM_OFF, ...MISSION_REALISM },
  extra: PRESET_EXTRAS.novice,
  // START NEAR THE ACTION: the original single mission's own default, and
  // every generator's shipped behaviour before this field existed (roadmap 10a).
  aerodromeStart: false,
};

/**
 * `OVERALL DIFFICULTY`, the panel's derived readout. Live values are the only
 * data: `4` at NOVICE (nothing on), `7` at INTERMEDIATE, `9` at EXPERT
 * (everything on). A floor of 4 plus five points spread across the eleven
 * checkboxes reproduces all three exactly; the *shape* is inferred, and only
 * the three preset readings pin it.
 */
export function overallDifficulty(panel: RealismPanel): number {
  const on = REALISM_TOGGLE_IDS.filter((id) => toggleValue(panel, id)).length;
  return 4 + Math.round((5 * on) / REALISM_TOGGLE_IDS.length);
}

/** Stamps the panel onto a freshly generated mission, which is how `/fly` comes to fly it. */
export function applyRealism(spec: MissionSpec, panel: RealismPanel): MissionSpec {
  return { ...spec, tier: panel.tier, realism: panel.sim, combatLevel: panel.combatLevel };
}

function normalizeTier(value: unknown): FlightModelTier {
  return FLIGHT_MODEL_TIERS.find((tier) => tier === value) ?? DEFAULT_REALISM.tier;
}

function normalizeCombatLevel(value: unknown): CombatLevel {
  return COMBAT_LEVELS.find((level) => level === value) ?? DEFAULT_REALISM.combatLevel;
}

/** One stored boolean, or the default when the payload has anything else there. */
function flag(candidate: unknown, key: string, fallback: boolean): boolean {
  if (typeof candidate !== "object" || candidate === null) return fallback;
  const value = (candidate as Record<string, unknown>)[key];
  return typeof value === "boolean" ? value : fallback;
}

function normalizeSim(candidate: unknown): RealismSettings {
  const base = DEFAULT_REALISM.sim;
  return {
    gunJams: flag(candidate, "gunJams", base.gunJams),
    blackouts: flag(candidate, "blackouts", base.blackouts),
    carburetorFreezes: flag(candidate, "carburetorFreezes", base.carburetorFreezes),
    limitedFuel: flag(candidate, "limitedFuel", base.limitedFuel),
    limitedAmmunition: flag(candidate, "limitedAmmunition", base.limitedAmmunition),
    aircraftMayBeDamaged: flag(candidate, "aircraftMayBeDamaged", base.aircraftMayBeDamaged),
    midairCollisions: flag(candidate, "midairCollisions", base.midairCollisions),
    realisticWeather: flag(candidate, "realisticWeather", base.realisticWeather),
  };
}

function normalizeExtra(candidate: unknown): PresentationRealism {
  const base = DEFAULT_REALISM.extra;
  return {
    realisticInstruments: flag(candidate, "realisticInstruments", base.realisticInstruments),
    sunBlindSpot: flag(candidate, "sunBlindSpot", base.sunBlindSpot),
    realNavigation: flag(candidate, "realNavigation", base.realNavigation),
  };
}

/** Reads whatever survives a `JSON.parse` of untrusted stored text, falling back per-field. */
export function normalizeRealism(candidate: unknown): RealismPanel {
  if (typeof candidate !== "object" || candidate === null) return DEFAULT_REALISM;
  const raw = candidate as {
    tier?: unknown;
    combatLevel?: unknown;
    sim?: unknown;
    extra?: unknown;
    aerodromeStart?: unknown;
  };
  return {
    tier: normalizeTier(raw.tier),
    combatLevel: normalizeCombatLevel(raw.combatLevel),
    sim: normalizeSim(raw.sim),
    extra: normalizeExtra(raw.extra),
    aerodromeStart: typeof raw.aerodromeStart === "boolean" ? raw.aerodromeStart : DEFAULT_REALISM.aerodromeStart,
  };
}

/**
 * Defaults on a missing key, corrupt JSON or a non-object payload — never
 * throws. `REALISTIC INSTRUMENTS` comes from the cockpit's own store so the
 * panel and the in-flight setting can never disagree.
 */
export function loadRealism(store: SettingsStore): RealismPanel {
  const raw = store.getItem(REALISM_STORAGE_KEY);
  const stored = raw === null ? DEFAULT_REALISM : parseRealism(raw);
  return { ...stored, extra: { ...stored.extra, realisticInstruments: loadCockpitSettings(store).realisticInstruments } };
}

function parseRealism(raw: string): RealismPanel {
  try {
    return normalizeRealism(JSON.parse(raw));
  } catch {
    return DEFAULT_REALISM;
  }
}

export function saveRealism(store: SettingsStore, panel: RealismPanel): void {
  store.setItem(REALISM_STORAGE_KEY, JSON.stringify(panel));
  saveCockpitSettings(store, { realisticInstruments: panel.extra.realisticInstruments });
}

/** The stored panel, or the default when the browser has no storage. */
export function currentRealism(store: SettingsStore | undefined): RealismPanel {
  return store ? loadRealism(store) : DEFAULT_REALISM;
}

function isToggleId(value: string): value is RealismToggleId {
  return (REALISM_TOGGLE_IDS as readonly string[]).includes(value);
}

/**
 * The panel's own commands. Returns `undefined` for a command the panel does
 * not own, so a surface can hand every command here first and fall through.
 * `RESTORE` means the settings this visit started with — the manual's
 * "reset to defaults" read as "undo what I just did", which is the only
 * behaviour a panel with an ACCEPT button can sensibly have.
 */
export function applyRealismCommand(panel: RealismPanel, command: string, restore: RealismPanel): RealismPanel | undefined {
  if (command.startsWith("preset:")) {
    const tier = FLIGHT_MODEL_TIERS.find((candidate) => candidate === command.slice(7));
    return tier ? presetPanel(tier, panel.combatLevel, panel.aerodromeStart) : undefined;
  }
  if (command.startsWith("toggle:")) {
    const id = command.slice(7);
    return isToggleId(id) ? withToggle(panel, id, !toggleValue(panel, id)) : undefined;
  }
  if (command === "flight-model") {
    const next = (FLIGHT_MODEL_TIERS.indexOf(panel.tier) + 1) % FLIGHT_MODEL_TIERS.length;
    return { ...panel, tier: FLIGHT_MODEL_TIERS[next] ?? panel.tier };
  }
  if (command === "aerodrome-start:on") return { ...panel, aerodromeStart: true };
  if (command === "aerodrome-start:off") return { ...panel, aerodromeStart: false };
  if (command === "restore") return restore;
  if (command === "combat") {
    const next = (COMBAT_LEVELS.indexOf(panel.combatLevel) + 1) % COMBAT_LEVELS.length;
    return { ...panel, combatLevel: COMBAT_LEVELS[next] ?? panel.combatLevel };
  }
  return undefined;
}
